import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Panel } from "@/components/common";

export default function Notifications() {
  const [s, setS] = useState(null);
  const [extra, setExtra] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const load = () => api.get("/settings/code-alerts").then((r) => { setS(r.data); setExtra(r.data.extra_recipients.join(", ")); }).catch((e) => setErr(errMsg(e)));
  useEffect(() => { load(); }, []);
  const save = async (patch = {}) => {
    try {
      const body = { enabled: s.enabled, compliance_users: s.compliance_users, extra_recipients: extra.split(/[,;\s]+/).filter(Boolean), ...patch };
      const r = await api.put("/settings/code-alerts", body); setS(r.data); setExtra(r.data.extra_recipients.join(", ")); toast.success("Code change alerts saved");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const test = async () => {
    setBusy(true);
    try { const r = await api.post("/settings/code-alerts/test", {}); toast.success(`Test alert sent to ${r.data.to}`); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (err) return <div className="max-w-3xl"><PageHeader title="Notifications" /><p className="text-sm text-slate-500" data-testid="notifications-error">{err}</p></div>;
  return (
    <div className="max-w-3xl" data-testid="notifications-page">
      <PageHeader title="Notifications" subtitle="Emails the app sends to your team." />
      <Panel title="Code change alerts" testid="code-alerts-panel">
        {!s ? <Loader2 className="h-4 w-4 animate-spin" /> : (
          <div className="space-y-4 text-sm text-slate-700">
            <label className="flex items-center justify-between gap-4">
              <span>One email per applied SAP code upload (SAP commodity codes or Materials) that moves at least one product between In / Out / Check / Missing code / In scope from date. Never for previews.</span>
              <Switch checked={s.enabled} onCheckedChange={(v) => save({ enabled: v })} data-testid="code-alerts-switch" />
            </label>
            <label className="flex items-center justify-between gap-4">
              <span>Send to every active Compliance user</span>
              <Switch checked={s.compliance_users} onCheckedChange={(v) => save({ compliance_users: v })} data-testid="code-alerts-compliance-switch" />
            </label>
            <div>
              <p className="mb-1 font-medium">Extra recipients</p>
              <div className="flex gap-2">
                <Input value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="name@company.com, other@company.com" data-testid="code-alerts-extra-input" />
                <Button variant="outline" onClick={() => save()} data-testid="code-alerts-save">Save</Button>
              </div>
            </div>
            <p className="text-xs text-slate-500" data-testid="code-alerts-recipients">Code change alerts: {s.enabled ? "On" : "Off"} · Recipients ({s.recipients.length}): {s.recipients.join(", ") || "none"}</p>
            <Button variant="outline" size="sm" onClick={test} disabled={busy} data-testid="code-alerts-test">{busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1.5 h-3.5 w-3.5" />}Send me a test alert</Button>
          </div>
        )}
      </Panel>
    </div>
  );
}
